// PWA utility functions and hooks for TopDial

import { useState, useEffect } from "react";

/**
 * Hook to detect online/offline status
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    const updateOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    // Set initial state
    updateOnlineStatus();

    // Listen for online/offline events
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  return isOnline;
}

/**
 * Hook to manage PWA installation
 */
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkIfInstalled = () => {
      if (window.matchMedia("(display-mode: standalone)").matches) {
        setIsInstalled(true);
      }
    };

    checkIfInstalled();

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log("[PWA] Install prompt available");
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log("[PWA] App installed");
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const installPWA = async () => {
    if (!deferredPrompt) {
      return false;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      console.log("[PWA] Install prompt result:", outcome);

      if (outcome === "accepted") {
        setIsInstallable(false);
        setDeferredPrompt(null);
        return true;
      }

      return false;
    } catch (error) {
      console.error("[PWA] Install prompt error:", error);
      return false;
    }
  };

  return {
    isInstallable,
    isInstalled,
    installPWA,
  };
}

/**
 * PWA Manager simplified for next-pwa compatibility
 */

/**
 * Hook to use PWA manager (simplified for next-pwa)
 */
export function usePWAManager() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && "serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        // New service worker has taken control
        setUpdateAvailable(false);
        window.location.reload();
      });

      // Check for updates periodically
      const checkForUpdates = () => {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((registration) => {
            registration.update();
          });
        });
      };

      // Check for updates every 30 minutes
      const updateInterval = setInterval(checkForUpdates, 30 * 60 * 1000);

      return () => clearInterval(updateInterval);
    }
  }, []);

  const updatePWA = async () => {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();

      for (const registration of registrations) {
        if (registration.waiting) {
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
        }
      }

      window.location.reload();
      return true;
    }
    return false;
  };

  return {
    isReady: true,
    updateAvailable,
    error: null,
    updatePWA,
  };
}

/**
 * Utility functions
 */
export const PWAUtils = {
  // Check if running as PWA
  isRunningAsPWA() {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  },

  // Check if device supports PWA
  isPWASupported() {
    return "serviceWorker" in navigator && "caches" in window;
  },

  // Get PWA display mode
  getDisplayMode() {
    if (window.matchMedia("(display-mode: standalone)").matches) {
      return "standalone";
    }
    if (window.matchMedia("(display-mode: fullscreen)").matches) {
      return "fullscreen";
    }
    if (window.matchMedia("(display-mode: minimal-ui)").matches) {
      return "minimal-ui";
    }
    return "browser";
  },

  // Format cache size
  formatCacheSize(bytes) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  },

  // Share API support
  canShare() {
    return "share" in navigator;
  },

  // Share content
  async share(data) {
    if (!this.canShare()) {
      return false;
    }

    try {
      await navigator.share(data);
      return true;
    } catch (error) {
      console.error("[PWA] Share failed:", error);
      return false;
    }
  },
};

const pwaExports = { useOnlineStatus, usePWAInstall, usePWAManager, PWAUtils };
export default pwaExports;
